import {EditButton} from '@github-ui/code-view-shared/components/EditButton'
import TableOfContentsPanel from '@github-ui/code-view-shared/components/TableOfContentsPanel'
import {DirectoryRichtextContent} from '@github-ui/code-view-shared/components/directory/DirectoryRichtextContent'
import {useEditPermissions} from '@github-ui/code-view-shared/contexts/EditPermissionsContext'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import {PreferredFileTypes, type RepoOverviewBlobPayload} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {editBlobPath, repositoryTreePath} from '@github-ui/paths'
import {useSearchParams} from '@github-ui/use-navigate'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {BookIcon, CodeOfConductIcon, LawIcon, ListUnorderedIcon, PeopleIcon} from '@primer/octicons-react'
import {ActionMenu, Spinner, UnderlineNav} from '@primer/react'
import {useCallback, useDeferredValue, useEffect, useEffectEvent, useMemo, useRef, type ReactNode} from 'react'
import {Blankslate, FeatureFlags} from '@primer/react/experimental'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import styles from './OverviewRepoFiles.module.css'
import {useQuery} from '@github-ui/react-query'

export function OverviewFiles({
  initialFiles,
  shouldRecommendReadme,
  isPersonalRepo,
  readmeHeaderSlot,
  noReadmeSlot,
}: {
  initialFiles: RepoOverviewBlobPayload[] | undefined
  shouldRecommendReadme: boolean
  isPersonalRepo: boolean
  /** Ask Copilot README-header entry, rendered on the right of the README tab bar. */
  readmeHeaderSlot?: ReactNode
  /** Copilot card shown when no README or community files exist. */
  noReadmeSlot?: ReactNode
}) {
  const repo = useCurrentRepository()
  const refInfo = useRefInfo()
  const {data: deferredOverviewFiles} = useQuery({
    queryKey: [repo.ownerLogin, repo.name, 'overview-files', refInfo.name],
    queryFn: async () => {
      const url = repositoryTreePath({
        repo: {
          ownerLogin: repo.ownerLogin,
          name: repo.name,
        },
        action: 'overview-files',
        commitish: refInfo.name,
      })
      const response = await verifiedFetchJSON(url)

      if (response.ok) {
        const data = await response.json()
        return data.files as RepoOverviewBlobPayload[]
      }

      return null
    },
    enabled: initialFiles?.some((file: RepoOverviewBlobPayload) => !file.loaded) ?? false,
  })

  const overviewFiles = deferredOverviewFiles || initialFiles

  const {readme, coc, licenses, securityPolicy, contributing, tabNames, initialSelectedTab} = useMemo(() => {
    const files = overviewFiles ?? []
    const readmeFile = files.find(file => file.preferredFileType === PreferredFileTypes.README)
    const cocFile = files.find(file => file.preferredFileType === PreferredFileTypes.CODE_OF_CONDUCT)
    const licenseFiles = files.filter(file => file.preferredFileType === PreferredFileTypes.LICENSE)
    const securityFile = files.find(file => file.preferredFileType === PreferredFileTypes.SECURITY)
    const contributingFile = files.find(file => file.preferredFileType === PreferredFileTypes.CONTRIBUTING)

    const names: string[] = []
    if (readmeFile || shouldRecommendReadme) names.push('readme-ov-file')
    if (cocFile) names.push('coc-ov-file')
    if (contributingFile) names.push('contributing-ov-file')
    for (const [index, license] of licenseFiles.entries()) {
      names.push(`${license.tabName}-${index + 1}-ov-file`)
    }
    if (securityFile) names.push('security-ov-file')

    let initial = 'readme-ov-file'
    if (!readmeFile && !shouldRecommendReadme) {
      if (cocFile) {
        initial = 'coc-ov-file'
      } else if (contributingFile) {
        initial = 'contributing-ov-file'
      } else if (licenseFiles.length > 0 && licenseFiles[0]) {
        initial = `${licenseFiles[0].tabName}-1-ov-file`
      } else if (securityFile) {
        initial = 'security-ov-file'
      }
    }

    return {
      readme: readmeFile,
      coc: cocFile,
      licenses: licenseFiles,
      securityPolicy: securityFile,
      contributing: contributingFile,
      tabNames: names,
      initialSelectedTab: initial,
    }
  }, [overviewFiles, shouldRecommendReadme])

  const containerRef = useRef<HTMLDivElement>(null)
  const {canEdit} = useEditPermissions()
  const [searchParams, setSearchParams] = useSearchParams()

  // The active tab is derived from the URL (?tab=) rather than local state, making the URL the
  // single source of truth. This keeps React Router (Data Router) and TanStack Router (UI
  // Service) consistent and lets the router own scroll restoration. Falls back to the computed
  // initial tab when ?tab= is absent or names a tab that isn't available.
  const tabParam = searchParams.get('tab')
  const selectedTab = tabParam && tabNames.includes(tabParam) ? tabParam : initialSelectedTab
  // Defer the (potentially expensive) rich-text content swap so a tab change updates the active
  // tab indicator immediately while the heavy DirectoryRichtextContent render happens in a
  // non-urgent pass; keeping the indicator responsive avoids blocking the interaction.
  const deferredTab = useDeferredValue(selectedTab)

  const readmeRef = useRef<HTMLAnchorElement>(null)
  const contributingRef = useRef<HTMLAnchorElement>(null)
  const securityRef = useRef<HTMLAnchorElement>(null)
  const cocRef = useRef<HTMLAnchorElement>(null)

  const onHashChange = useEffectEvent(() => {
    const hash = window.location.hash.replace('#', '')
    if (!hash) return

    // Legacy anchor links (e.g. #coc-ov-file from the mobile repo header and Marketplace
    // resources) encode the active tab in the hash. Migrate them to the ?tab= query param via
    // the router so the derived tab updates across both routers. Read window.location.hash
    // directly (not the router): in-page hash changes fire `hashchange` but not `popstate`, so
    // the router never observes them.
    const isLegacyTabHash = tabNames.includes(hash)
    if (isLegacyTabHash) {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.set('tab', hash)
      setSearchParams(nextParams, {replace: true, preventScrollReset: true})
    }

    // Bring the overview into view (and focus the matching tab) for legacy tab hashes and the
    // #readme section anchor. `block: 'nearest'` avoids scrolling when the container is already
    // in view, and `preventScroll` on focus() stops the browser from running a second
    // scroll/layout pass right after scrollIntoView — Safari is especially sensitive to the
    // back-to-back reflow during this handler.
    if (isLegacyTabHash || hash === 'readme') {
      window.requestAnimationFrame(() => {
        if (!containerRef.current) return
        containerRef.current.scrollIntoView({block: 'nearest'})
        if (hash === 'readme-ov-file') {
          readmeRef.current?.focus({preventScroll: true})
        } else if (hash === 'coc-ov-file') {
          cocRef.current?.focus({preventScroll: true})
        } else if (hash === 'contributing-ov-file') {
          contributingRef.current?.focus({preventScroll: true})
        } else if (hash === 'security-ov-file') {
          securityRef.current?.focus({preventScroll: true})
        }
      })
    }
  })

  useEffect(() => {
    onHashChange()

    const listener = () => onHashChange()
    window.addEventListener('hashchange', listener)

    return () => {
      window.removeEventListener('hashchange', listener)
    }
  }, [])

  const onSelect = useCallback(
    (ev: React.MouseEvent | React.KeyboardEvent, tabId: string) => {
      ev.preventDefault()
      // Skip a redundant same-tab navigation. This guard stays out here because
      // `setSearchParams` calls the router's `navigate` unconditionally; an updater can't bail.
      if (selectedTab === tabId) return

      // Sync the active tab to the URL through the router (rather than a raw
      // history.replaceState) so scroll position is preserved. This works across both
      // React Router (Data Router) and TanStack Router (UI Service): `preventScrollReset`
      // is honored natively by React Router and mapped to TanStack's `resetScroll: false`.
      // Without this, TanStack patches history.replaceState and treats the URL change as a
      // navigation, resetting scroll to the top of the page. `replace` preserves the prior
      // replaceState semantics (no extra history entry per tab click).
      //
      // No functional updater: this isn't batchable React state, and both router adapters
      // resolve an updater's `prev` to this render's `searchParams` anyway, so reading it
      // directly is equivalent.
      const nextParams = new URLSearchParams(searchParams)
      nextParams.set('tab', tabId)
      setSearchParams(nextParams, {replace: true, preventScrollReset: true})
    },
    [selectedTab, searchParams, setSearchParams],
  )

  const selectedLicense = licenses.find((license, index) => `${license.tabName}-${index + 1}-ov-file` === deferredTab)

  let blob: RepoOverviewBlobPayload | undefined
  let content: React.ReactElement | undefined

  if (deferredTab === 'readme-ov-file') {
    if (readme) {
      blob = readme
    } else if (shouldRecommendReadme) {
      content = (
        <Blankslate>
          <Blankslate.Visual>
            <BookIcon size="medium" />
          </Blankslate.Visual>
          <Blankslate.Heading>Add a README</Blankslate.Heading>
          <Blankslate.Description>
            {isPersonalRepo
              ? 'Add a README with an overview of your project.'
              : 'Help people interested in this repository understand your project.'}
          </Blankslate.Description>
          <Blankslate.PrimaryAction
            href={`${repositoryTreePath({
              repo,
              path: undefined,
              commitish: refInfo.name,
              action: 'new',
            })}?filename=README.md`}
          >
            Add a README
          </Blankslate.PrimaryAction>
        </Blankslate>
      )
    }
  } else if (deferredTab === 'coc-ov-file' && coc) {
    blob = coc
  } else if (deferredTab === 'contributing-ov-file' && contributing) {
    blob = contributing
  } else if (selectedLicense) {
    blob = selectedLicense
  } else if (securityPolicy) {
    blob = securityPolicy
  }

  if (blob?.path) {
    if (!blob.loaded) {
      content = (
        <div className={styles.Box}>
          <Spinner />
        </div>
      )
    } else {
      content = (
        <DirectoryRichtextContent
          errorMessage={blob.errorMessage}
          path={blob.path}
          richText={blob.richText}
          stickyHeaderHeight={50}
          timedOut={blob.timedOut}
        />
      )
    }
  }

  if (!readme && !coc && !licenses.length && !securityPolicy && !shouldRecommendReadme && !contributing) {
    return noReadmeSlot ? (
      <div className={styles.Box_1}>
        <div className={styles.Box_2}>{noReadmeSlot}</div>
      </div>
    ) : null
  }

  return (
    <div className={styles.Box_1}>
      <div ref={containerRef} className={styles.Box_2}>
        <div itemScope itemType="https://schema.org/abstract" className={styles.Box_3}>
          <UnderlineNav aria-label="Repository files" className={styles.UnderlineNav}>
            {(readme || shouldRecommendReadme) && (
              <UnderlineNav.Item
                leadingVisual={<BookIcon />}
                ref={readmeRef}
                aria-current={selectedTab === 'readme-ov-file' ? 'page' : undefined}
                onSelect={ev => onSelect(ev, 'readme-ov-file')}
              >
                {readme?.tabName || 'README'}
              </UnderlineNav.Item>
            )}
            {coc && (
              <UnderlineNav.Item
                leadingVisual={<CodeOfConductIcon />}
                ref={cocRef}
                aria-current={selectedTab === 'coc-ov-file' ? 'page' : undefined}
                onSelect={ev => onSelect(ev, 'coc-ov-file')}
              >
                {coc?.tabName || 'Code of conduct'}
              </UnderlineNav.Item>
            )}
            {contributing && (
              <UnderlineNav.Item
                leadingVisual={<PeopleIcon />}
                ref={contributingRef}
                aria-current={selectedTab === 'contributing-ov-file' ? 'page' : undefined}
                onSelect={ev => onSelect(ev, 'contributing-ov-file')}
              >
                {contributing?.tabName || 'Contributing'}
              </UnderlineNav.Item>
            )}
            {licenses.length
              ? licenses.map((license, index) => {
                  const tabId = `${license.tabName}-${index + 1}-ov-file`

                  return (
                    <UnderlineNav.Item
                      key={license.path}
                      leadingVisual={<LawIcon />}
                      aria-current={selectedTab === tabId ? 'page' : undefined}
                      onSelect={ev => onSelect(ev, tabId)}
                    >
                      {license.tabName?.toLowerCase() !== 'license' ? `${license.tabName} license` : 'License'}
                    </UnderlineNav.Item>
                  )
                })
              : null}
            {securityPolicy && (
              <UnderlineNav.Item
                leadingVisual={<LawIcon />}
                ref={securityRef}
                aria-current={selectedTab === 'security-ov-file' ? 'page' : undefined}
                onSelect={ev => onSelect(ev, 'security-ov-file')}
              >
                {securityPolicy?.tabName || 'Security policy'}
              </UnderlineNav.Item>
            )}
          </UnderlineNav>
          <div className={styles.readmeHeaderSlot}>{readmeHeaderSlot}</div>
          {canEdit && blob && (
            <EditButton
              editPath={editBlobPath({
                owner: repo.ownerLogin,
                repo: blob.repoName,
                commitish: blob.refName,
                filePath: blob.path,
              })}
              editTooltip="Edit file"
              className={styles.editButton}
            />
          )}
          {blob?.headerInfo?.toc && blob?.headerInfo?.toc?.length >= 2 && (
            <FeatureFlags flags={{primer_react_css_anchor_positioning: isFeatureEnabled('prs_css_anchor_positioning')}}>
              <ActionMenu>
                <ActionMenu.Button
                  icon={ListUnorderedIcon}
                  variant="invisible"
                  aria-label="Outline"
                  className={styles.ActionMenu_Button}
                >
                  Outline
                </ActionMenu.Button>

                <ActionMenu.Overlay align="end" className={styles.ActionMenu_Overlay}>
                  <TableOfContentsPanel toc={blob.headerInfo.toc} />
                </ActionMenu.Overlay>
              </ActionMenu>
            </FeatureFlags>
          )}
        </div>
        {content}
      </div>
    </div>
  )
}
